import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Grid,
  InputField,
  IconButton
} from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import type { OptionsDocument } from 'paintscout'
import { updateEventType } from '@paintscout/util/builder'
import type { EventTypeOption } from '@paintscout/util/builder'
import clone from 'lodash/clone'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      position: 'relative',
      display: 'grid',
      gridGap: '0.75em',
      justifyContent: 'space-between',
      gridTemplateColumns: `repeat(1, 1fr)`
    },
    dragging: {
      zIndex: 2147483002
    },
    dialogTitle: {},
    dialogContent: {},
    stageBody: {
      background: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      boxShadow: 'none',
      height: '100%'
    },
    listBody: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      height: '100%',
      gap: theme.spacing(2)
    },
    tiles: {},
    tile: {},
    stageLabel: {},
    addStageButton: {
      width: '88%',
      marginRight: theme.spacing(1),
      alignSelf: 'center'
    },
    dragIcon: {
      color: theme.paintscout.palette.brand.Neutral.P90,
      cursor: 'grab',
      alignSelf: 'flex-end',
      fontSize: '2.5rem',
      margintRight: theme.spacing(1)
    },
    removeButton: {
      marginTop: theme.spacing(2.5),
      marginLeft: theme.spacing(2.5)
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'EventTypeDialog' }
)

export interface EventTypeDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  options: OptionsDocument
  eventType: EventTypeOption
  isNew: boolean
  onConfirm: (updatedOptions: OptionsDocument, isDirty: boolean) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

function EventTypeDialog(props: EventTypeDialogProps) {
  const classes = useStyles()
  const { onConfirm, onCancel, options, eventType, isNew, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isDirty, setIsDirty] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentEventType, setCurrentEventType] = useState(clone(eventType))

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        }
      >
        <Typography variant="h2">{isNew ? 'Create' : 'Edit'} Event Type</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form
          id="event-type-option"
          onSubmit={(ev) => {
            ev.preventDefault()
            handleSubmit()
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputField
                classes={{ root: classes.input }}
                label={'Name'}
                name={'label'}
                value={currentEventType.label}
                fullWidth={true}
                onChange={handleInputChange}
                required
                error={!currentEventType.label}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.confirm}
          type="submit"
          form="event-type-option"
          variant={'contained'}
          disabled={isLoading}
          data-testid="event-type-dialog-confirm-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      const updatedOptions = updateEventType({ options, eventType: currentEventType })
      onConfirm(updatedOptions, isDirty)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = event.target
    setCurrentEventType((prev) => ({
      ...prev,
      [name]: value
    }))
    setIsDirty(true)
  }
}

export default EventTypeDialog
